import { createServerFn } from "@tanstack/react-start";
import { PRICE_KEYS, coursesIncludedFor, type PurchaseType } from "@/lib/products";
import { type StripeEnv, createStripeClient, getStripeErrorMessage } from "@/lib/stripe.server";

export interface CheckoutLineItem {
  priceId: string;
  quantity: number;
}

export interface BuyerDetails {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  /** Certification chosen when buying the three-course bundle. */
  certification?: string;
  /**
   * Training Pipeline certification id. The only Training Pipeline identifier
   * carried through checkout, kept for post-purchase certification processing.
   */
  certificationId?: string;
  /** Scheduled session chosen when buying a single course. */
  course?: string;
  /** Scheduler session id, first date, time, and course id for a single course. */
  sessionId?: string;
  sessionDate?: string;
  sessionTime?: string;
  courseId?: string;
  /** Training Pipeline session id for each meeting of the chosen cohort. */
  tpSessionIds?: number[];
}
export interface CheckoutSummary {
  status: string | null;
  paymentStatus: string | null;
  email: string | null;
  amountTotal: number | null;
  currency: string | null;
  seats: number;
  coachingSeats: number;
  /** Training Pipeline certification id, for post-purchase processing. */
  certificationId: string | null;
  lines: { description: string; quantity: number; amount: number }[];
}

type CheckoutSessionResult = { clientSecret: string } | { error: string };

const SAFE_ID = /^[a-zA-Z0-9_-]+$/;

async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId?: string; name?: string },
): Promise<string> {
  if (options.userId && !SAFE_ID.test(options.userId)) {
    throw new Error("Invalid userId");
  }
  if (options.userId) {
    const found = await stripe.customers.search({
      query: `metadata['userId']:'${options.userId}'`,
      limit: 1,
    });
    if (found.data.length && found.data[0]) return found.data[0].id;
  }
  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    const customer = existing.data[0];
    if (customer) {
      if (options.userId && customer.metadata?.["userId"] !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
        });
      }
      return customer.id;
    }
  }
  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    ...(options.name && { name: options.name }),
    ...(options.userId && { metadata: { userId: options.userId } }),
  });
  return created.id;
}

/**
 * How many courses the chosen certification covers, straight from Training
 * Pipeline. Returns 0 when the certification can't be resolved, so we never
 * record a guessed course count.
 */
async function certificationCourseCount(certificationId?: string): Promise<number> {
  if (!certificationId) return 0;
  try {
    const { getCertifications } = await import("@/lib/training-pipeline.server");
    const certifications = await getCertifications();
    const match = certifications.find((cert) => String(cert.id) === certificationId);
    return match?.courses.length ?? 0;
  } catch (error) {
    console.error("[payments] could not resolve certification course count", error);
    return 0;
  }
}

export const createLicenseCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      items: CheckoutLineItem[];
      customerEmail?: string;
      userId?: string;
      buyer?: BuyerDetails;
      returnUrl: string;
      environment: StripeEnv;
      mode?: "subscription" | "payment";
      purchaseType?: PurchaseType;
    }) => {
      if (!Array.isArray(data.items) || data.items.length === 0) {
        throw new Error("No items selected");
      }
      for (const item of data.items) {
        if (!SAFE_ID.test(item.priceId)) throw new Error("Invalid priceId");
        if (!Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 500) {
          throw new Error("Invalid quantity");
        }
      }
      if (data.mode && data.mode !== "subscription" && data.mode !== "payment") {
        throw new Error("Invalid mode");
      }
      return data;
    },
  )
  .handler(async ({ data }): Promise<CheckoutSessionResult> => {
    try {
      const stripe = createStripeClient(data.environment);
      const mode = data.mode ?? "subscription";

      const lineItems: { price: string; quantity: number }[] = [];
      for (const item of data.items) {
        const prices = await stripe.prices.list({ lookup_keys: [item.priceId] });
        const price = prices.data[0];
        if (!price) {
          console.error(
            `[payments] no Stripe price found for lookup key "${item.priceId}" in ${data.environment}`,
          );
          return {
            error: `This item is not available for purchase yet (missing price: ${item.priceId}). Please contact support.`,
          };
        }
        lineItems.push({ price: price.id, quantity: item.quantity });
      }

      const customerId = await resolveOrCreateCustomer(stripe, {
        email: data.customerEmail,
        userId: data.userId,
        name: data.buyer?.fullName,
      });

      const quantityOf = (lookupKey: string) =>
        data.items.find((i) => i.priceId === lookupKey)?.quantity ?? 0;
      const purchaseType: PurchaseType =
        data.purchaseType ?? (mode === "payment" ? "single_course" : "annual_license");
      // Never 0: an organization license uses its seat count; every individual
      // purchase (yearly or one-time) is exactly one person.
      const seats =
        purchaseType === "annual_license"
          ? Math.max(quantityOf(PRICE_KEYS.businessUnlimited), 1)
          : 1;
      // A certification covers however many courses Training Pipeline lists for
      // it, so read that from the API rather than assuming a fixed number.
      const coursesIncluded =
        purchaseType === "certification"
          ? await certificationCourseCount(data.buyer?.certificationId)
          : coursesIncludedFor(purchaseType);
      // Coaching rides on the two yearly plans, each with its own price:
      // organizations buy seats, an individual buys exactly one.
      const coachingSeats =
        purchaseType === "annual_license"
          ? quantityOf(PRICE_KEYS.businessCoaching)
          : purchaseType === "individual_unlimited"
            ? Math.min(quantityOf(PRICE_KEYS.individualCoaching), 1)
            : 0;

      // Comma-separated Training Pipeline meeting ids, capped at Stripe's 500 chars.
      const tpIds = (Array.isArray(data.buyer?.tpSessionIds) ? data.buyer.tpSessionIds : []).filter(
        (n) => Number.isInteger(n) && n > 0,
      );
      const tpSessionIds = tpIds.length ? tpIds.join(",").slice(0, 500) : "";
      const metadata: Record<string, string> = {
        seats: String(seats),
        coaching_seats: String(coachingSeats),
        purchase_type: purchaseType,
        courses_included: String(coursesIncluded),
        ...(data.customerEmail && { email: data.customerEmail }),
        ...(data.userId && { userId: data.userId }),
        ...(data.buyer?.fullName && { full_name: data.buyer.fullName }),
        ...(data.buyer?.firstName && { first_name: data.buyer.firstName }),
        ...(data.buyer?.lastName && { last_name: data.buyer.lastName }),
        ...(data.buyer?.company && { company: data.buyer.company }),
        ...(data.buyer?.phone && { phone: data.buyer.phone }),
        ...(data.buyer?.certification && { certification: data.buyer.certification }),
        ...(data.buyer?.certificationId && { certification_id: data.buyer.certificationId }),
        ...(data.buyer?.course && { course: data.buyer.course.slice(0, 450) }),
        ...(data.buyer?.sessionId && { scheduler_session_id: data.buyer.sessionId.slice(0, 100) }),
        ...(data.buyer?.sessionDate && { session_date: data.buyer.sessionDate.slice(0, 20) }),
        ...(data.buyer?.sessionTime && { session_time: data.buyer.sessionTime.slice(0, 60) }),
        ...(data.buyer?.courseId &&
          /^\d+$/.test(data.buyer.courseId) && { course_id: data.buyer.courseId }),
        ...(tpSessionIds && { tp_session_ids: tpSessionIds }),
      };

      const params = {
        line_items: lineItems,
        mode,
        ui_mode: "embedded_page" as const,
        return_url: data.returnUrl,
        customer: customerId,
        metadata,
        ...(mode === "subscription" && { subscription_data: { metadata } }),
      };

      try {
        const session = await stripe.checkout.sessions.create({
          ...params,
          automatic_tax: { enabled: true },
        });
        return { clientSecret: session.client_secret ?? "" };
      } catch (taxError) {
        const message = getStripeErrorMessage(taxError);
        if (!/tax/i.test(message)) throw taxError;
        const session = await stripe.checkout.sessions.create(params);
        return { clientSecret: session.client_secret ?? "" };
      }
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const getCheckoutSummary = createServerFn({ method: "POST" })
  .inputValidator((data: { sessionId: string; environment: StripeEnv }) => {
    if (!/^[a-zA-Z0-9_-]+$/.test(data.sessionId)) throw new Error("Invalid session id");
    return data;
  })
  .handler(async ({ data }): Promise<{ summary: CheckoutSummary } | { error: string }> => {
    try {
      const stripe = createStripeClient(data.environment);
      const session = await stripe.checkout.sessions.retrieve(data.sessionId, {
        expand: ["line_items"],
      });
      return {
        summary: {
          status: session.status ?? null,
          paymentStatus: session.payment_status ?? null,
          email: session.customer_details?.email ?? null,
          amountTotal: session.amount_total,
          currency: session.currency,
          seats: Number(session.metadata?.["seats"] ?? 0),
          coachingSeats: Number(session.metadata?.["coaching_seats"] ?? 0),
          certificationId: session.metadata?.["certification_id"] ?? null,
          lines: (session.line_items?.data ?? []).map((line) => ({
            description: line.description ?? "License",
            quantity: line.quantity ?? 1,
            amount: line.amount_total ?? 0,
          })),
        },
      };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
